import { Check, Copy, Eye, EyeOff, KeyRound, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useWatch, type FieldValues, type UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { PLATFORM_CREDENTIAL_LABEL, type PlatformCredential } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { checkPassword, generateStrongPassword } from './password-utils';

async function copyText(text: string, ok: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(ok);
    return true;
  } catch {
    toast.error('Não foi possível copiar. Selecione o texto e copie manualmente.');
    return false;
  }
}

/** Medidor e requisitos da senha (sem a senha em si). */
export function PasswordMeter({ password }: { password: string }) {
  const c = checkPassword(password);
  return (
    <div className="grid gap-1.5" aria-live="polite">
      <div className="flex gap-1" role="img" aria-label={password ? `Força da senha: ${c.label}` : 'Força da senha'}>
        {[1, 2, 3, 4].map((i) => (
          <span key={i} className={cn('h-1.5 flex-1 rounded-full bg-muted', c.score >= i && (c.score >= 3 ? 'bg-primary' : c.score === 2 ? 'bg-warning' : 'bg-destructive'))} />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
        {c.rules.map((r) => (
          <li key={r.label} className={cn('flex items-center gap-1', r.ok ? 'text-primary' : 'text-muted-foreground')}>
            <Check className={cn('size-3', !r.ok && 'opacity-30')} aria-hidden /> {r.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Escolha da credencial inicial: "Definir senha agora" (com medidor, gerador e copiar) ou "Enviar link".
 * Os valores ficam no formulário do pai em `${base}credential` e `${base}password` (campo de senha não controlado; só este bloco observa a senha).
 */
export function CredentialFields<T extends FieldValues>({
  form,
  base = '',
  idPrefix,
  errorPassword,
}: {
  form: UseFormReturn<T>;
  /** prefixo do caminho no formulário, ex.: 'owner.' */
  base?: string;
  idPrefix: string;
  errorPassword?: string;
}) {
  const f = form as unknown as UseFormReturn<FieldValues>;
  const credential = (useWatch({ control: f.control, name: `${base}credential` }) ?? 'link') as PlatformCredential;
  const password = (useWatch({ control: f.control, name: `${base}password` }) ?? '') as string;
  const [show, setShow] = useState(false);
  const pwId = `${idPrefix}-password`;
  return (
    <div className="grid gap-3">
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-sm font-medium">Acesso inicial</legend>
        <RadioGroup value={credential} onValueChange={(v) => f.setValue(`${base}credential`, v, { shouldDirty: true })} className="grid gap-2 sm:grid-cols-2" aria-label="Acesso inicial">
          {(['password', 'link'] as const).map((c) => (
            <Label key={c} htmlFor={`${idPrefix}-cred-${c}`} className={cn('flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm font-normal', credential === c && 'border-primary bg-accent/40')}>
              <RadioGroupItem id={`${idPrefix}-cred-${c}`} value={c} />
              {PLATFORM_CREDENTIAL_LABEL[c]}
            </Label>
          ))}
        </RadioGroup>
        <p className="text-xs text-muted-foreground">
          {credential === 'password'
            ? 'Você define a senha e a repassa à pessoa. Ela precisará trocá-la no primeiro acesso.'
            : 'Gera um link de uso único (vale 7 dias) para a pessoa criar a própria senha. Sem e-mail configurado, o link aparece só para você, uma vez.'}
        </p>
      </fieldset>
      {credential === 'password' && (
        <div className="grid gap-2">
          <Field id={pwId} label="Senha inicial" error={errorPassword}>
            <div className="flex flex-wrap gap-2">
              <div className="relative min-w-0 flex-1 basis-48">
                <Input
                  id={pwId}
                  type={show ? 'text' : 'password'}
                  autoComplete="new-password"
                  aria-invalid={!!errorPassword}
                  className="pr-10 font-mono"
                  {...f.register(`${base}password`)}
                />
                <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? 'Ocultar senha' : 'Mostrar senha'} className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground">
                  {show ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
                </button>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  f.setValue(`${base}password`, generateStrongPassword(), { shouldDirty: true, shouldValidate: true });
                  setShow(true);
                }}
              >
                <KeyRound className="size-4" aria-hidden /> Gerar senha forte
              </Button>
              <Button type="button" variant="outline" disabled={!password} onClick={() => copyText(password, 'Senha copiada')} aria-label="Copiar senha">
                <Copy className="size-4" aria-hidden /> Copiar
              </Button>
            </div>
          </Field>
          <PasswordMeter password={password} />
        </div>
      )}
    </div>
  );
}

/** Resultado da criação: link de definição de senha (exibido uma única vez) ou lembrete da troca de senha. */
export function CredentialResult({ setupUrl, emailSent, created = true, who }: { credential: PlatformCredential; setupUrl: string | null; emailSent: boolean; created?: boolean; who: string }) {
  const [copied, setCopied] = useState(false);
  if (!created) {
    return <p className="text-sm">{who} já tinha conta e foi vinculado(a) à organização. A senha atual dela foi mantida.</p>;
  }
  if (setupUrl) {
    return (
      <div className="grid gap-4 text-sm">
        <div role="alert" className="flex gap-3 rounded-md border border-brass/50 bg-brass/10 p-3">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-ink" aria-hidden />
          <p>
            <strong>Este link é exibido uma única vez.</strong> Copie e envie para {who}. Ele vale por 7 dias.{' '}
            {emailSent ? 'O link também foi enviado por e-mail.' : 'O e-mail não foi enviado, porque o envio de e-mails não está configurado.'}
          </p>
        </div>
        <Field id="setup-url" label="Link para definir a senha">
          <div className="flex flex-wrap gap-2">
            <Input id="setup-url" readOnly value={setupUrl} onFocus={(e) => e.currentTarget.select()} className="min-w-0 flex-1 basis-48 font-mono text-xs" />
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                if (await copyText(setupUrl, 'Link copiado')) setCopied(true);
              }}
            >
              {copied ? <Check aria-hidden /> : <Copy aria-hidden />} {copied ? 'Copiado' : 'Copiar link'}
            </Button>
          </div>
        </Field>
      </div>
    );
  }
  return (
    <p className="text-sm">
      A senha foi definida. {who} precisará trocá-la no primeiro acesso: o sistema pede uma nova senha antes de liberar as telas.
    </p>
  );
}
